"use client";
import { Image } from "@/components/Image";
import { SlideTransition } from "@/components/useSlideTransition";
import clsx from "clsx";
import { useState } from "react";
import { ArrowLeftIcon, ArrowRightIcon } from "ui/iconsN";
import styles from "./Images.module.css";

const slideStyles = {
  enterRight: styles.fadeEnterRight,
  enterActiveRight: styles.fadeEnterActiveRight,
  exitRight: styles.fadeExitRight,
  exitActiveRight: styles.fadeExitActiveRight,
  enterLeft: styles.fadeEnterLeft,
  enterActiveLeft: styles.fadeEnterActiveLeft,
  exitLeft: styles.fadeExitLeft,
  exitActiveLeft: styles.fadeExitActiveLeft,
};

type Props = {
  type: "hotel" | "boat";
  owner: {
    id: number;
    title: string;
    images: Array<{ id: number; name: string; realname: string; caption: string }>;
  };
  sizes: string;
  showDisclaimer?: boolean;
  className?: string;
};
export const PhotoCarousel = ({ type, owner, sizes, showDisclaimer = true, className }: Props) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState<"right" | "left">("right");

  const currentImage = owner.images[currentIndex];

  const handlePrevious = () => {
    setDirection("left");
    setCurrentIndex((i) => (i === 0 ? i : i - 1));
  };

  const handleNext = () => {
    setDirection("right");
    setCurrentIndex((i) => (i === owner.images.length - 1 ? i : i + 1));
  };

  if (!currentImage) return null;

  return (
    <div className={clsx("relative overflow-hidden select-none", className)}>
      <SlideTransition
        currentKey={currentImage.id.toString()}
        currentData={currentImage}
        direction={direction}
        timeout={250}
        styles={slideStyles}
      >
        {(image, transitionClassName) => (
          <div className={`absolute top-0 w-full h-full ${transitionClassName}`}>
            <Image
              alt={image.caption || owner.title}
              sizes={sizes}
              image={{ type, id: owner.id.toString(), name: image.name, realname: image.realname }}
              objectFit="cover"
              className="h-full"
            />
          </div>
        )}
      </SlideTransition>
      {showDisclaimer && (
        <div className="absolute inset-x-0 bottom-0 h-[30px] bg-linear-to-b from-black/0 to-black/70 pointer-events-none">
          <p className="absolute left-2 right-2 top-2 text-xs/4 text-white opacity-70 truncate">
            *Photos non contractuelles
          </p>
        </div>
      )}
      {currentIndex !== 0 && (
        <button
          type="button"
          aria-label="Photo précédente"
          className="group absolute left-0 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white w-8 h-16 cursor-pointer rounded-r-full flex justify-center items-center backdrop-blur-xs border border-l-0 border-white/0 shadow-[0_0_16px_0_rgba(0,0,0,0.24)] hover:border-pr-1 hover:shadow-[0_0_24px_0_rgba(185,87,51,0.70)] transition-all"
          onClick={handlePrevious}
        >
          <ArrowLeftIcon className="group-hover:-translate-x-1 transition-transform stroke-tx-1" />
        </button>
      )}
      {currentIndex !== owner.images.length - 1 && (
        <button
          type="button"
          aria-label="Photo suivante"
          className="group absolute right-0 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white w-8 h-16 cursor-pointer rounded-l-full flex justify-center items-center backdrop-blur-xs border border-r-0 border-white/0 shadow-[0_0_16px_0_rgba(0,0,0,0.24)] hover:border-pr-1 hover:shadow-[0_0_24px_0_rgba(185,87,51,0.70)] transition-all"
          onClick={handleNext}
        >
          <ArrowRightIcon className="group-hover:translate-x-1 transition-transform stroke-tx-1" />
        </button>
      )}
    </div>
  );
};
